import { MilestonesToBeBilledQueryParams } from '@/services/billing/billing.service';
import { ChoicesStatusMilestone } from '@/types/milestone.interface';
import Iconify from '@components/media/iconify';
import {
  Grid,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  InputAdornment,
} from '@mui/material';
import { debounce } from 'lodash';
import moment from 'moment';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface MilestoneSelectorFilterProps {
  filters: MilestonesToBeBilledQueryParams;
  handleChangeFilters: (
    filterAttrName: keyof MilestonesToBeBilledQueryParams,
    value: string
  ) => void;
}

export const MilestoneSelectorFilterSection = ({
  filters,
  handleChangeFilters,
}: MilestoneSelectorFilterProps) => {
  const { t } = useTranslation();

  const statusOptions = useMemo(
    () => [
      { _id: ChoicesStatusMilestone.SCHEDULED, label: t('Schedule') },
      { _id: ChoicesStatusMilestone.OPEN_TO_BILL, label: t('Open to Bill') },
      { _id: ChoicesStatusMilestone.PAST_DUE, label: t('Past Due') },
    ],
    [t]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        handleChangeFilters('search', value);
      }, 500),
    [handleChangeFilters]
  );

  const monthsDetailed = Array.from({ length: 12 }, (_, i) => ({
    number: moment().utc().month(i).format('MM'),
    name: moment().utc().month(i).format('MMMM'),
  }));

  const years = Array.from({ length: 5 }, (_, i) =>
    moment().utc().subtract(i, 'year').format('YYYY')
  );

  return (
    <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
      {/* Search Input */}
      <Grid item md={8}>
        <TextField
          sx={{ minWidth: 250 }}
          fullWidth
          defaultValue={filters.search}
          onChange={(e) => {
            const { value } = e.target;
            debouncedSearch(value);
          }}
          placeholder={t('Search...')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      {/* Year Input */}
      <Grid item md={1.2}>
        <FormControl size="small" sx={{ width: '100%' }}>
          <InputLabel>{t('Year')}</InputLabel>
          <Select
            value={filters.year ? String(filters.year) : ''}
            label={t('Year')}
            onChange={(e) => handleChangeFilters('year', e.target.value)}
          >
            <MenuItem value="">{t('Every Year')}</MenuItem>
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Month Input */}
      <Grid item md={1.2}>
        <FormControl size="small" sx={{ width: '100%' }}>
          <InputLabel>{t('Month')}</InputLabel>
          <Select
            value={filters.month ? String(filters.month) : ''}
            label={t('Month')}
            onChange={(e) => handleChangeFilters('month', e.target.value)}
          >
            <MenuItem value="">{t('Every Month')}</MenuItem>
            {monthsDetailed.map((month) => (
              <MenuItem key={month.number} value={month.number}>
                {month.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Status Input */}
      <Grid item md={1.6}>
        <FormControl size="small" sx={{ width: '100%' }}>
          <InputLabel>{t('Status')}</InputLabel>
          <Select
            value={filters.status ? filters.status : ''}
            label={t('Status')}
            onChange={(e) => handleChangeFilters('status', e.target.value)}
          >
            <MenuItem value="">{t('Every Status')}</MenuItem>
            {statusOptions.map((option) => (
              <MenuItem key={option._id} value={option._id}>
                {t(option.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};
